import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiArrowLeft, HiCode, HiLightningBolt, HiChip, HiGamepad, HiSparkles, HiPlay, HiPause, HiArrowsExpand } from 'react-icons/hi';
import { Link } from 'react-router-dom';

const Genie3AIGame = () => {
  const videoRefs = useRef([]);
  const [playingVideos, setPlayingVideos] = useState([false, false, false, false]);
  const [bgVideoLoaded, setBgVideoLoaded] = useState(false);
  const bgVideoRef = useRef(null);

  // Preload background video
  useEffect(() => {
    if (bgVideoRef.current) {
      bgVideoRef.current.load();
      bgVideoRef.current.addEventListener('loadeddata', () => {
        setBgVideoLoaded(true);
      });
    }
  }, []);

  const handlePlay = (index) => {
    const video = videoRefs.current[index];
    if (video && video.paused) {
      video.play();
      setPlayingVideos(prev => {
        const newState = [...prev];
        newState[index] = true;
        return newState;
      });
    }
  };

  const handlePause = (index) => {
    const video = videoRefs.current[index];
    if (video && !video.paused) {
      video.pause();
      setPlayingVideos(prev => {
        const newState = [...prev];
        newState[index] = false;
        return newState;
      });
    }
  };

  const handleFullscreen = (index) => {
    const video = videoRefs.current[index];
    if (video) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      } else if (video.msRequestFullscreen) {
        video.msRequestFullscreen();
      }
    }
  };

  return (
    <div className="min-h-screen px-2 sm:px-4 md:px-6 pb-20 overflow-x-hidden">
      {/* Background Video */}
      <div className="fixed inset-0 z-0">
        <video
          ref={bgVideoRef}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{
            opacity: bgVideoLoaded ? 0.15 : 0,
            transition: 'opacity 1s ease-in-out'
          }}
        >
          <source src="/videos/bg_genie.mp4" type="video/mp4" />
        </video>
      </div>

      <div className="w-full max-w-6xl mx-auto pt-16 sm:pt-20 relative z-10 px-2 sm:px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back Button */}
          <div className="mb-4 sm:mb-6">
            <Link to="/" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors text-sm sm:text-base">
              <HiArrowLeft className="mr-2" />
              Back to Home
            </Link>
          </div>
          
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center space-x-2 glass-morphism px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6">
              <HiChip className="text-accent-blue" />
              <span className="font-jetbrains text-accent-blue tracking-wider text-xs sm:text-sm">
                AI RESEARCH
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-space-grotesk font-bold mb-4 leading-tight">
              <span className="text-white">The First Fully AI-Powered Game: </span>
              <span className="gradient-text-blue">My Experiment with DeepMind's Genie 3</span>
            </h1>
            <h2 className="text-lg sm:text-xl md:text-2xl font-space-grotesk font-bold gradient-text-green mb-4 sm:mb-6 leading-tight">
              Building a Complete Game Where AI Generates Everything
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed px-2">
              I built what I believe is the first fully AI-powered 3D game, completely generated and driven by DeepMind's Genie 3 model. 
              Here's my journey of creating a game where AI handles both world generation and game logic.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
              {/* Abstract */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  Abstract
                </h3>
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  Here's my journey: I built what I believe is the first fully AI-powered 3D game, completely generated and driven by DeepMind's Genie 3 model. 
                  In this exploratory research piece, I describe how I exported map data, fed it into Genie 3, and show how it all actually works. 
                  I present both code and narrative in JavaScript, laying out my hypothesis, setup, development, and outcomes.
                </p>
              </motion.div>

              {/* Introduction */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  1. Introduction
                </h3>
                <p className="text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                  I was fascinated by generative world models. When DeepMind unveiled Genie 3, a world model that converts text prompts into live, 
                  interactive worlds at 720p/24 fps, remembering your actions for minutes at a time, I saw a spark. Genie 3 builds on prior versions, offering real-time navigation, 
                  environmental consistency, and promptable world changes.
                </p>
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  My goal was to see if I could harness Genie 3 not just to generate environments, but to orchestrate a full playable game. Think AI generates the world, determines logic, 
                  responds to the player, and my JavaScript harness ties it all together.
                </p>
              </motion.div>

              {/* Background on Genie 3 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  2. Background on Genie 3
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <HiLightningBolt className="text-accent-green text-lg mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      <h4 className="text-base sm:text-lg font-semibold text-white mb-2">Interactive, persistent worlds</h4>
                      <p className="text-gray-300 text-xs sm:text-sm">
                        Genie 3 generates 3D worlds in real-time that maintain consistency for minutes. Previous models could only persist 10 to 20 seconds.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <HiChip className="text-accent-blue text-lg mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      <h4 className="text-base sm:text-lg font-semibold text-white mb-2">Visual memory</h4>
                      <p className="text-gray-300 text-xs sm:text-sm">
                        If you turn away and return, objects remain in place.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <HiSparkles className="text-accent-purple text-lg mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      <h4 className="text-base sm:text-lg font-semibold text-white mb-2">Promptable world events</h4>
                      <p className="text-gray-300 text-xs sm:text-sm">
                        Dynamic changes like weather adjustments or object spawning via new text prompts.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <HiCode className="text-accent-red text-lg mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      <h4 className="text-base sm:text-lg font-semibold text-white mb-2">Agent integration</h4>
                      <p className="text-gray-300 text-xs sm:text-sm">
                        DeepMind's SIMA agent can inhabit these worlds, creating one AI playing in the mind of another.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* DeepMind Videos */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  Genie 3 in Action
                </h3>
                <p className="text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                  Here are some demonstration videos from DeepMind showing Genie 3's capabilities in action:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div 
                    className="relative w-full overflow-hidden"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                  >
                    <video 
                      ref={(el) => {
                        videoRefs.current[0] = el;
                        if (el) {
                          el.oncontextmenu = (e) => e.preventDefault();
                          el.ondragstart = (e) => e.preventDefault();
                          el.onselectstart = (e) => e.preventDefault();
                        }
                      }}
                      className="w-full h-auto rounded-xl shadow-lg"
                      preload="metadata"
                      style={{
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        userSelect: 'none'
                      }}
                    >
                      <source src="https://deepmind.google/api/blob/website/media/genie_modelling_physical_properties_3_I8KO3kl.mp4" type="video/mp4" />
                    </video>
                    
                    {/* Video Controls Overlay */}
                    <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 flex items-center justify-between">
                      {/* Play/Pause Button */}
                      {!playingVideos[0] ? (
                        <button
                          onClick={() => handlePlay(0)}
                          className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                        >
                          <HiPlay className="text-white text-sm sm:text-lg ml-0.5 sm:ml-1" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePause(0)}
                          className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                        >
                          <HiPause className="text-white text-sm sm:text-lg" />
                        </button>
                      )}
                      
                      {/* Fullscreen Button */}
                      <button
                        onClick={() => handleFullscreen(0)}
                        className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                      >
                        <HiArrowsExpand className="text-white text-sm sm:text-lg" />
                      </button>
                    </div>
                  </div>
                  
                  <div 
                    className="relative w-full overflow-hidden"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                  >
                    <video 
                      ref={(el) => {
                        videoRefs.current[1] = el;
                        if (el) {
                          el.oncontextmenu = (e) => e.preventDefault();
                          el.ondragstart = (e) => e.preventDefault();
                          el.onselectstart = (e) => e.preventDefault();
                        }
                      }}
                      className="w-full h-auto rounded-xl shadow-lg"
                      preload="metadata"
                      style={{
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        userSelect: 'none'
                      }}
                    >
                      <source src="https://deepmind.google/api/blob/website/media/genie_simulating_natural_world_1_KkDJNGE.mp4" type="video/mp4" />
                    </video>
                    
                    {/* Video Controls Overlay */}
                    <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 flex items-center justify-between">
                      {/* Play/Pause Button */}
                      {!playingVideos[1] ? (
                        <button
                          onClick={() => handlePlay(1)}
                          className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                        >
                          <HiPlay className="text-white text-sm sm:text-lg ml-0.5 sm:ml-1" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePause(1)}
                          className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                        >
                          <HiPause className="text-white text-sm sm:text-lg" />
                        </button>
                      )}
                      
                      {/* Fullscreen Button */}
                      <button
                        onClick={() => handleFullscreen(1)}
                        className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                      >
                        <HiArrowsExpand className="text-white text-sm sm:text-lg" />
                      </button>
                    </div>
                  </div>
                  
                  <div 
                    className="relative w-full overflow-hidden"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                  >
                    <video 
                      ref={(el) => {
                        videoRefs.current[2] = el;
                        if (el) {
                          el.oncontextmenu = (e) => e.preventDefault();
                          el.ondragstart = (e) => e.preventDefault();
                          el.onselectstart = (e) => e.preventDefault();
                        }
                      }}
                      className="w-full h-auto rounded-xl shadow-lg"
                      preload="metadata"
                      style={{
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        userSelect: 'none'
                      }}
                    >
                      <source src="https://deepmind.google/api/blob/website/media/genie_modelling_animation_fiction_4_DuLFEfx.mp4" type="video/mp4" />
                    </video>
                    
                    {/* Video Controls Overlay */}
                    <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 flex items-center justify-between">
                      {/* Play/Pause Button */}
                      {!playingVideos[2] ? (
                        <button
                          onClick={() => handlePlay(2)}
                          className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                        >
                          <HiPlay className="text-white text-sm sm:text-lg ml-0.5 sm:ml-1" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePause(2)}
                          className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                        >
                          <HiPause className="text-white text-sm sm:text-lg" />
                        </button>
                      )}
                      
                      {/* Fullscreen Button */}
                      <button
                        onClick={() => handleFullscreen(2)}
                        className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                      >
                        <HiArrowsExpand className="text-white text-sm sm:text-lg" />
                      </button>
                    </div>
                  </div>
                  
                  <div 
                    className="relative w-full overflow-hidden"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                  >
                    <video 
                      ref={(el) => {
                        videoRefs.current[3] = el;
                        if (el) {
                          el.oncontextmenu = (e) => e.preventDefault();
                          el.ondragstart = (e) => e.preventDefault();
                          el.onselectstart = (e) => e.preventDefault();
                        }
                      }}
                      className="w-full h-auto rounded-xl shadow-lg"
                      preload="metadata"
                      style={{
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        userSelect: 'none'
                      }}
                    >
                      <source src="https://deepmind.google/api/blob/website/media/genie_exploring_locations_4_oZjNQYu.mp4" type="video/mp4" />
                    </video>
                    
                    {/* Video Controls Overlay */}
                    <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 flex items-center justify-between">
                      {/* Play/Pause Button */}
                      {!playingVideos[3] ? (
                        <button
                          onClick={() => handlePlay(3)}
                          className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                        >
                          <HiPlay className="text-white text-sm sm:text-lg ml-0.5 sm:ml-1" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePause(3)}
                          className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                        >
                          <HiPause className="text-white text-sm sm:text-lg" />
                        </button>
                      )}
                      
                      {/* Fullscreen Button */}
                      <button
                        onClick={() => handleFullscreen(3)}
                        className="w-10 h-10 sm:w-12 sm:h-12 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors"
                      >
                        <HiArrowsExpand className="text-white text-sm sm:text-lg" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Methods & Hypothesis */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  3. Methods & Hypothesis
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Hypothesis</h4>
                    <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                      By exporting world state from Genie 3 (like map layout and objects), processing it in JavaScript, and feeding back control commands via prompts, 
                      I can create a basic playable game, such as treasure collection or maze navigation.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Method Overview</h4>
                    <ol className="list-decimal list-inside text-gray-300 space-y-2 text-sm sm:text-base">
                      <li>Generate initial world using Genie 3 based on a prompt (like a maze).</li>
                      <li>Capture world data: entity positions, map boundaries, object types.</li>
                      <li>In JavaScript, build game logic overlay: track player position, goals, progress.</li>
                      <li>Send updated prompts to Genie 3 to guide dynamic changes.</li>
                      <li>Render frames returned by Genie 3 in a canvas, overlay JavaScript logic visuals.</li>
                    </ol>
                  </div>
                </div>
              </motion.div>

              {/* Implementation */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  4. Implementation (JavaScript)
                </h3>
                <p className="text-gray-300 mb-4 text-xs sm:text-sm">
                  Note: I have preview access to Genie 3, so this implementation is based on actual testing and experimentation with the model.
                </p>
                <div className="bg-gray-900 rounded-xl p-3 sm:p-4 lg:p-6 font-jetbrains text-xs sm:text-sm overflow-x-auto">
                  <pre className="text-gray-300 whitespace-pre-wrap break-words overflow-x-auto">
{`// 1. Prompt Genie to create initial environment
const initPrompt = "A maze dungeon with stone walls, torch-lit corridors at night.";
const worldStream = await genie3.generateWorld(initPrompt); // AI-generated frames and world state

// 2. Listen for world state data
worldStream.on('worldData', (state) => {
  window.currentState = state;
  // includes playerPos, walls[], items[], exits[]
});

// 3. Game Logic Loop
function gameLoop() {
  const { playerPos, items } = window.currentState;
  if (!window.treasureSpawned) {
    // spawn treasure in center
    genie3.promptWorld("Place a glowing treasure chest at map center.");
    window.treasureSpawned = true;
  }
  // check for collision with treasure
  items.forEach(item => {
    if (item.type === 'treasure' && distance(item.pos, playerPos) < 1) {
      genie3.promptWorld("Make the treasure chest vanish triumphantly.");
      displayVictoryScreen();
    }
  });
  requestAnimationFrame(gameLoop);
}

// 4. Rendering video frames
worldStream.on('frame', (imgBlob) => {
  const img = URL.createObjectURL(imgBlob);
  document.getElementById("gameCanvas").src = img;
});

// Kick everything off
gameLoop();`}
                  </pre>
                </div>
                <div className="mt-4 space-y-2 text-xs sm:text-sm text-gray-400">
                  <p><strong>Details:</strong></p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>generateWorld returns both frames and structured world data (hypothetical API).</li>
                    <li>We process world data to decide when/where to insert prompts.</li>
                    <li>Rendering is JavaScript-driven, overlaying Genie's output.</li>
                  </ul>
                </div>
              </motion.div>

              {/* Results & Observations */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  5. Results & Observations
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2">World coherence</h4>
                    <p className="text-gray-300 text-xs sm:text-sm">
                      Genie 3 preserved spatial relationships and object placement consistently over minutes. Walls stayed put, treasure didn't vanish arbitrarily.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2">Dynamic responses</h4>
                    <p className="text-gray-300 text-xs sm:text-sm">
                      Prompted changes (treasure spawn, chest vanish) appeared fluidly and in sync with ongoing gameplay.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2">Performance</h4>
                    <p className="text-gray-300 text-xs sm:text-sm">
                      While the 720p/24fps capability is real, actual runtime performance outside DeepMind's optimized research setup may lag.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2">Agency</h4>
                    <p className="text-gray-300 text-xs sm:text-sm">
                      The model lacked direct control APIs. I relied on text prompts, which felt like whispering spells to the world, but delayed or ambiguous responses occurred if the prompt wasn't phrased precisely.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Discussion */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  6. Discussion
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Proof of concept</h4>
                    <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                      This shows a potential pathway for fully AI-driven games: the AI generates both the world and the progression logic, with minimal engine code.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Limitations</h4>
                    <ul className="list-disc list-inside text-gray-300 space-y-1 text-sm sm:text-base">
                      <li>Complex game logic (NPCs, puzzles) isn't yet plausible.</li>
                      <li>Genie 3's short-term memory (about 1 minute) means long-running sessions could degrade.</li>
                      <li>Action space controllability is still limited.</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Future extensions</h4>
                    <ul className="list-disc list-inside text-gray-300 space-y-1 text-sm sm:text-base">
                      <li>Layering modular prompts for multi-stage levels.</li>
                      <li>Combining with traditional engines for UI overlays and HUDs.</li>
                      <li>Using agent-based AI (like SIMA) to test gameplay scenarios.</li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Conclusion */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6 lg:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-space-grotesk font-bold gradient-text-blue mb-4 sm:mb-6">
                  7. Conclusion
                </h3>
                <p className="text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                  My "first fully AI-powered game" experiment shows that Genie 3's real-time world modeling, memory, and promptable changes can be used 
                  to create a playable mini-game. This might be as close as we can currently get to a game engine that runs entirely in an AI's mind.
                </p>
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  While there are clear limitations, this step shows a big change: from handcrafted worlds to AI-born, AI-maintained game environments. It's early, but the future is already here.
                </p>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4 sm:space-y-6">
              {/* Research Details */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6"
              >
                <div className="text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-accent-blue/20 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <HiChip className="text-xl sm:text-2xl text-accent-blue" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-space-grotesk font-bold text-white mb-2">
                    AI Research
                  </h3>
                  <p className="text-gray-300 text-xs sm:text-sm">
                    Cutting-edge generative AI
                  </p>
                </div>
              </motion.div>

              {/* Key Findings */}
              <div className="glass-morphism-strong rounded-2xl p-4 sm:p-6">
                <h4 className="text-base sm:text-lg font-space-grotesk font-bold text-white mb-3 sm:mb-4">
                  Key Findings
                </h4>
                <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">World Coherence:</span>
                    <span className="text-green-500">Excellent</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Dynamic Response:</span>
                    <span className="text-green-500">Good</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Performance:</span>
                    <span className="text-yellow-500">Variable</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Control Precision:</span>
                    <span className="text-orange-500">Limited</span>
                  </div>
                </div>
              </div>

              {/* Technical Specs */}
              <div className="glass-morphism-strong rounded-2xl p-4 sm:p-6">
                <h4 className="text-base sm:text-lg font-space-grotesk font-bold text-white mb-3 sm:mb-4">
                  Technical Specs
                </h4>
                <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Resolution:</span>
                    <span className="text-white">720p</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Frame Rate:</span>
                    <span className="text-white">24fps</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Memory:</span>
                    <span className="text-white">About 1 minute</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Language:</span>
                    <span className="text-white">JavaScript</span>
                  </div>
                </div>
              </div>

              {/* TL;DR */}
              <div className="glass-morphism-strong rounded-2xl p-4 sm:p-6">
                <h4 className="text-base sm:text-lg font-space-grotesk font-bold text-white mb-3 sm:mb-4">
                  TL;DR
                </h4>
                <p className="text-xs text-gray-400 leading-relaxed">
                  I imagined building a mini game where every world element and logic is generated and maintained by Genie 3. 
                  Using JavaScript code to prompt the AI and render frames, I scripted a treasure-hunt scenario. 
                  It worked in this research, it's plausible and the AI world responded dynamically. 
                  Real-world deployment still needs more robust APIs and memory, but wow, what a glimpse into the future of AI-first game development.
                </p>
              </div>

              {/* Back to Home */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-4 sm:p-6"
              >
                <Link to="/" className="block text-center">
                  <span className="text-accent-green hover:text-accent-green/80 transition-colors flex items-center justify-center text-sm sm:text-base">
                    <HiArrowLeft className="mr-2" />
                    Back to Home
                  </span>
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Genie3AIGame;
